import { Link as RouterLink, Navigate as RouterNavigate, type LinkProps, type NavigateProps } from "react-router-dom";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { localizePath } from "@/lib/i18n/localize-path";
import { linkLocaleFor } from "@/lib/i18n/locale-availability";
import type { Locale } from "@/lib/i18n/config";

/**
 * Prefixes internal absolute paths with the current locale; leaves external/hash/mailto links
 * untouched.
 *
 * The prefix is the locale the page actually exists in, not blindly the current one — see
 * locale-availability.ts. An item with no translated name has no page in this locale, so linking
 * it under the locale prefix would point at a 404 the prerender deliberately never wrote.
 */
function resolveTo<T extends LinkProps["to"]>(to: T, locale: Locale): T {
  if (typeof to !== "string") return to;
  if (/^([a-z]+:)?\/\//i.test(to) || to.startsWith("#") || to.startsWith("mailto:") || to.startsWith("tel:")) {
    return to as T;
  }
  return localizePath(to, linkLocaleFor(to, locale)) as T;
}

/** Drop-in replacement for react-router-dom's Link that keeps the current locale prefix on internal navigation. */
export function Link({ to, ...props }: LinkProps) {
  const { locale } = useLocale();
  return <RouterLink to={resolveTo(to, locale)} {...props} />;
}

/** Drop-in replacement for react-router-dom's Navigate with the same locale-prefixing behavior. */
export function Navigate({ to, ...props }: NavigateProps) {
  const { locale } = useLocale();
  return <RouterNavigate to={resolveTo(to, locale)} {...props} />;
}
